import { Auth0Client, createAuth0Client } from "@auth0/auth0-spa-js";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Api, fetchConfig, type PublicConfig } from "./api";

const DEV_TOKEN_KEY = "fabula.devToken";

interface AuthState {
  ready: boolean;
  config: PublicConfig | null;
  loggedIn: boolean;
  error: string | null;
  api: Api;
  login: (devName?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

function readDevToken(): string | null {
  try {
    return localStorage.getItem(DEV_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [client, setClient] = useState<Auth0Client | null>(null);
  const [devToken, setDevToken] = useState<string | null>(readDevToken);
  const [auth0LoggedIn, setAuth0LoggedIn] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await fetchConfig();
        setConfig(cfg);
        if (cfg.auth_mode === "auth0") {
          if (!cfg.auth0_domain || !cfg.auth0_client_id) {
            throw new Error("Auth0 is niet geconfigureerd (domain/client id ontbreekt).");
          }
          const c = await createAuth0Client({
            domain: cfg.auth0_domain,
            clientId: cfg.auth0_client_id,
            authorizationParams: {
              audience: cfg.auth0_audience ?? undefined,
              redirect_uri: window.location.origin,
            },
            // Not "memory": with refresh tokens the SDK then runs a Web Worker from a blob: URL,
            // which the CSP (script-src 'self') blocks, so the code exchange after login never
            // finishes. Memory would also lose the session on every reload (the iframe fallback
            // needs third-party cookies). Refresh tokens are rotated (Auth0 app setting).
            cacheLocation: "localstorage",
            useRefreshTokens: true,
          });
          const params = new URLSearchParams(window.location.search);
          if (params.has("state") && (params.has("code") || params.has("error"))) {
            // Clean the address first, so a failed callback does not repeat on every reload.
            const callbackUrl = window.location.href;
            window.history.replaceState({}, "", window.location.pathname + window.location.hash);
            if (params.has("error")) {
              // E.g. "Service not found" (audience) or a login Action that denies access.
              const reason = params.get("error_description") || params.get("error");
              throw new Error(`Inloggen mislukt: ${reason}`);
            }
            await c.handleRedirectCallback(callbackUrl);
          }
          setAuth0LoggedIn(await c.isAuthenticated());
          setClient(c);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const value = useMemo<AuthState>(() => {
    const token = async () => {
      if (config?.auth_mode === "dev") {
        if (!devToken) throw new Error("niet ingelogd");
        return devToken;
      }
      if (!client) throw new Error("niet ingelogd");
      const t = await client.getTokenSilently();
      if (!t) throw new Error("niet ingelogd");
      return t;
    };
    return {
      ready,
      config,
      error,
      loggedIn: config?.auth_mode === "dev" ? !!devToken : auth0LoggedIn,
      api: new Api(
        token,
        (config?.wiki_url ?? "").replace(/\/$/, ""),
        (config?.api_url ?? "").replace(/\/$/, ""),
      ),
      login: async (devName?: string) => {
        if (config?.auth_mode === "dev") {
          const name = (devName ?? "").trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
          if (!name) return;
          const t = `dev:${name}`;
          try {
            localStorage.setItem(DEV_TOKEN_KEY, t);
          } catch {
            /* private mode: token lives in memory only */
          }
          setDevToken(t);
        } else {
          await client?.loginWithRedirect();
        }
      },
      logout: () => {
        if (config?.auth_mode === "dev") {
          try {
            localStorage.removeItem(DEV_TOKEN_KEY);
          } catch {
            /* ignore */
          }
          setDevToken(null);
        } else {
          client?.logout({ logoutParams: { returnTo: window.location.origin } });
        }
      },
    };
  }, [ready, config, error, client, devToken, auth0LoggedIn]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
